/**
 * Modal de Confirmação de Exclusão
 * Usado para confirmar exclusão de arquivos, pastas ou itens em lote
 */

import React from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  alpha,
  keyframes,
  useMediaQuery,
  useTheme
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import FolderIcon from '@mui/icons-material/Folder'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-2px); }
  75% { transform: translateX(2px); }
`

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`

export type DeleteItemType = 'file' | 'folder' | 'batch'

export interface DeleteItem {
  id: string
  name: string
  type: 'file' | 'folder'
}

interface ConfirmDeleteDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  /** Tipo do item sendo excluído */
  itemType: DeleteItemType
  /** Nome do item (para exclusão individual) */
  itemName?: string
  /** Lista de itens (para exclusão em lote) */
  items?: DeleteItem[]
  /** Está processando a exclusão */
  isDeleting?: boolean
}

export default function ConfirmDeleteDialog({
  open,
  onClose,
  onConfirm,
  itemType,
  itemName,
  items = [],
  isDeleting = false
}: ConfirmDeleteDialogProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const isBatch = itemType === 'batch'
  const isFolder = itemType === 'folder'
  const count = isBatch ? items.length : 1

  const getTitle = () => {
    if (isBatch) {
      return `Excluir ${count} ${count === 1 ? 'item' : 'itens'}?`
    }
    if (isFolder) {
      return 'Excluir pasta?'
    }
    return 'Excluir arquivo?'
  }

  const getMessage = () => {
    if (isBatch) {
      const fileCount = items.filter(i => i.type === 'file').length
      const folderCount = items.filter(i => i.type === 'folder').length
      const parts = []
      if (fileCount > 0) parts.push(`${fileCount} arquivo${fileCount > 1 ? 's' : ''}`)
      if (folderCount > 0) parts.push(`${folderCount} pasta${folderCount > 1 ? 's' : ''}`)
      return `Você está prestes a excluir ${parts.join(' e ')}. Todos os itens dentro das pastas também serão excluídos. Esta ação não pode ser desfeita.`
    }
    if (isFolder) {
      return `A pasta "${itemName}" será excluída permanentemente, incluindo todos os arquivos e subpastas dentro dela. Esta ação não pode ser desfeita.`
    }
    return `O arquivo "${itemName}" será excluído permanentemente. Esta ação não pode ser desfeita.`
  }

  return (
    <Dialog
      open={open}
      onClose={isDeleting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          animation: `${fadeIn} 0.2s ease-out`
        }
      }}
    >
      <DialogTitle sx={{ pb: 1, px: { xs: 2, sm: 3 }, pt: { xs: 1.5, sm: 2 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, sm: 2 }}>
            <Box
              sx={{
                width: { xs: 40, sm: 48 },
                height: { xs: 40, sm: 48 },
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha('#f44336', 0.1),
                color: 'error.main',
                animation: `${shake} 0.5s ease-in-out`
              }}
            >
              <WarningAmberIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
            </Box>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              {getTitle()}
            </Typography>
          </Stack>
          <IconButton onClick={onClose} size="small" disabled={isDeleting}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, px: { xs: 2, sm: 3 } }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
          {getMessage()}
        </Typography>

        {/* Lista de itens para exclusão em lote */}
        {isBatch && items.length > 0 && (
          <Box
            sx={{
              maxHeight: 200,
              overflow: 'auto',
              bgcolor: alpha('#f44336', 0.03),
              borderRadius: 2,
              border: '1px solid',
              borderColor: alpha('#f44336', 0.1),
              p: 1
            }}
          >
            <Stack spacing={0.5}>
              {items.slice(0, 10).map((item) => (
                <Stack
                  key={item.id}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    bgcolor: 'background.paper'
                  }}
                >
                  {item.type === 'folder' ? (
                    <FolderIcon sx={{ fontSize: 18, color: '#90CAF9' }} />
                  ) : (
                    <InsertDriveFileIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {item.name}
                  </Typography>
                </Stack>
              ))}
              {items.length > 10 && (
                <Typography variant="caption" color="text.secondary" sx={{ pl: 1.5, pt: 0.5 }}>
                  ... e mais {items.length - 10} {items.length - 10 === 1 ? 'item' : 'itens'}
                </Typography>
              )}
            </Stack>
          </Box>
        )}

        {/* Item individual */}
        {!isBatch && itemName && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.5,
              bgcolor: alpha('#f44336', 0.03),
              borderRadius: 2,
              border: '1px solid',
              borderColor: alpha('#f44336', 0.1)
            }}
          >
            {isFolder ? (
              <FolderIcon sx={{ fontSize: 24, color: '#90CAF9' }} />
            ) : (
              <InsertDriveFileIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
            )}
            <Typography
              variant="body2"
              fontWeight={500}
              sx={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {itemName}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: { xs: 1.5, sm: 2 },
          gap: 1,
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          '& > button': { width: { xs: '100%', sm: 'auto' } }
        }}
      >
        <Button onClick={onClose} color="inherit" disabled={isDeleting} sx={{ minHeight: { xs: 44, sm: 36 } }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={isDeleting}
          startIcon={isDeleting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <DeleteOutlineIcon />}
          sx={{
            px: { xs: 2, sm: 3 },
            minHeight: { xs: 48, sm: 36 },
            '&:hover': {
              bgcolor: 'error.dark'
            },
            ...(isDeleting && {
              animation: `${pulse} 1.5s ease-in-out infinite`,
              pointerEvents: 'none'
            })
          }}
        >
          {isDeleting ? 'Excluindo...' : 'Excluir'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
